<script setup lang="ts">
import { DefinitionParameterDto, UnitDto } from '@/api/generator';
import VeeNumberField from '@/components/forms/NumberField/VeeNumberField.vue';
import Select from '@/components/forms/Select/Select.vue';
import VeeSelect from '@/components/forms/Select/VeeSelect.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import { useUnitsStore } from '@/stores/unitsStore.ts';
import { idEqual } from '@/utils/compareAndSort';
import {
  decimalsOptions,
  defaultDecimals,
  valueTypeOptions,
} from '@/views/quantities/components/quantityFormHelpers';
import { useField } from 'vee-validate';
import { onMounted, ref, toRef, watch } from 'vue';
import Range from '../Range.vue';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';

const props = defineProps<{
  name: string;
  allowExtend: boolean;
}>();

const emits = defineEmits<{ remove: [] }>();

const { value, setValue } = useField<DefinitionParameterDto>(toRef(() => props.name));

const unitsStore = useUnitsStore();
const selectedUnit = ref<UnitDto | undefined>(undefined);

function updateSelectedUnit(unit: UnitDto | undefined): void {
  selectedUnit.value = unit;
  setValue({ ...value.value, unitId: unit?.id });
}
onMounted(() => {
  if (value.value?.unitId) {
    selectedUnit.value = unitsStore.getById(value.value.unitId);
  }
});

function resetValues() {
  value.value.decimalPlaces = value.value.valueType
    ? defaultDecimals[value.value.valueType]
    : undefined;
  value.value.inputRangeMax = undefined;
  value.value.inputRangeMin = undefined;
}

watch(
  () => value.value.valueType,
  (valueType, oldValueType) => {
    if (valueType && oldValueType) {
      resetValues();
    }
  },
);

watch(
  () => selectedUnit.value,
  (unitNew, unitOld) => {
    if (unitNew && unitOld) {
      resetValues();
    }
  },
);
</script>

<template>
  <li class="container">
    <hr class="mb-8 hr" />
    <div class="d-flex mb-8">
      <v-container class="m-0 no-padding">
        <v-row noGutters>
          <v-col cols="11">
            <v-row noGutters>
              <v-col cols="5">
                <!-- Content for item 1 -->
                <VeeTextField
                  :label="$t('Name')"
                  :name="`${name}.name`"
                  :placeholder="$t('Required')"
                  class="mr-6 mb-4"
                  :disabled="allowExtend"
                />
              </v-col>
              <v-col cols="3">
                <VeeSelect
                  :name="`${name}.valueType`"
                  :label="$t('ValueType')"
                  :items="valueTypeOptions"
                  class="mr-6 mb-4"
                  :disabled="allowExtend"
                />
              </v-col>
              <v-col cols="2">
                <Select
                  :label="$t('Unit_one')"
                  :clearable="true"
                  :items="unitsStore.options"
                  :valueComparator="idEqual"
                  :modelValue="selectedUnit"
                  class="mr-6"
                  :disabled="allowExtend"
                  @update:modelValue="updateSelectedUnit"
                />
              </v-col>
              <v-col cols="2">
                <VeeSelect
                  :disabled="value.valueType === 'IntegerNumber' || allowExtend"
                  :items="decimalsOptions"
                  :label="$t('DisplayedDecimals')"
                  :placeholder="$t('Required')"
                  :name="`${name}.decimalPlaces`"
                />
              </v-col>
            </v-row>

            <v-row noGutters>
              <v-col cols="5">
                <VeeTextarea
                  :label="$t('Description')"
                  :name="`${name}.description`"
                  class="mr-6"
                  :disabled="allowExtend"
                />
              </v-col>

              <v-col cols="7">
                <Range :label="$t('Range')" :disabled="allowExtend">
                  <template #min>
                    <VeeNumberField
                      class="w-50"
                      :name="`${name}.inputRangeMin`"
                      :prefix="$t('Min')"
                      :suffix="selectedUnit?.abbreviation"
                      :format="{ valueType: value.valueType }"
                      :disabled="allowExtend"
                    />
                  </template>

                  <template #max>
                    <VeeNumberField
                      class="w-50"
                      :name="`${name}.inputRangeMax`"
                      :prefix="$t('Max')"
                      :suffix="selectedUnit?.abbreviation"
                      :format="{ valueType: value.valueType }"
                      :disabled="allowExtend"
                    />
                  </template>
                </Range>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="1" class="d-flex justify-center align-center">
            <v-divider
              class="ml-3"
              vertical
              thickness="1"
              color="secondary-lighten-3"
              length="100%"
            ></v-divider>
            <PtwDeleteButton :disabled="allowExtend" @delete="emits('remove')" />
          </v-col>
        </v-row>
      </v-container>
    </div>
  </li>
</template>

<style scoped lang="scss">
.container {
  .hr {
    height: 1px;
    background-color: rgb(var(--v-theme-primary-lighten-4));
    border: none;
  }

  &:first-child {
    .hr {
      display: none !important;
    }
  }

  .no-padding {
    padding: 0 !important;
  }
  .remove-btn {
    cursor: pointer;
    display: flex;

    align-items: center;
    padding: 0 5px;
    border-radius: 2px;

    margin-left: 30px;
    border-left: 1px solid rgb(var(--v-theme-primary-lighten-4));

    &:hover {
      background-color: rgb(var(--v-theme-secondary), 0.15);
    }
  }
}
</style>
